import { Component, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { BnNgIdleService } from 'bn-ng-idle';
import { ConfirmationService } from 'primeng/api';
import { StorageService } from './services/storage.service';
import { Spinkit, SpinnerVisibilityService } from 'ng-http-loader';
import { Constants } from './models/constants';
import { EventType } from '@azure/msal-browser';
import { Subject, Subscription, interval, take, takeUntil } from 'rxjs';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'Global Admin';
  loggedIn = false;
  SCREEN_ID = '';
  userEmail = '';
  comments: {
    id: number;
    userIcon: string;
    firstName: string;
    lastName: string;
    userEmail: string;
    screenId: string;
    createdTime: Date;
    comment: string;
  }[] = [];
  public spinkit = Spinkit;
  isEditMode = false;
  isUpdateComment = false;
  commentIndex = -1;
  updateCommentIndex = -1;
  currentEditComment: any;
  displayBasic: boolean = false;
  counter = 0;
  showDropdown = false;
  destroy$ = new Subject<void>();
  timerSubscription: Subscription;
  constructor(
    private router: Router,
    private storageService: StorageService,
    private confirmationService: ConfirmationService,
    private bnIdle: BnNgIdleService,
    private msalBroadCastService: MsalBroadcastService,
    private spinner: SpinnerVisibilityService, private msalService: MsalService
  ) {


  }

  ngOnInit() {
    this.isLoggedIn();
    this.msalBroadCastService.msalSubject$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (result: any) => {
        if (result.eventType == EventType.INITIALIZE_START) {
          this.spinner.show();
        } else if (result.eventType == EventType.LOGIN_SUCCESS || result.eventType == EventType.ACQUIRE_TOKEN_SUCCESS) {
          if (result?.payload?.account?.username) {
            this.msalService.instance.setActiveAccount(result?.payload?.account);
          }
          if (result?.payload?.idToken) {
            this.storageService.setCookies(result?.payload?.idTokenClaims?.nonce, 'nonce');
            this.proceedForLogin(result?.payload?.idToken);
          } else {
            for (let i = 0; i < sessionStorage.length; i++) {
              const key = sessionStorage.key(i);
              if (key[0].includes('-idtoken-')) {
                const idToken = JSON.parse(sessionStorage.getItem(key))?.secret;
                this.proceedForLogin(idToken);
                break;
              }
            }
          }
        } else if (result.eventType === EventType.LOGIN_FAILURE) {
          this.spinner.hide();
          this.router.navigate(['/']);
          this.loggedIn = false;
        } else if (result.eventType === EventType.HANDLE_REDIRECT_END) {
          this.spinner.hide();
        }
      },
      error: (error) => {
        this.spinner.hide();
        console.log('error', error);
      },
      complete: () => {
        this.spinner.hide();
      }
    });

    this.storageService.concurrFound$.subscribe((res) => {
      if(res)
        this.concurrentPopup();
    });
  }

  proceedForLogin(idToken) {
    this.storageService.setSessionStorage('msal.idtoken', idToken);
    this.storageService.setCookies(this.storageService?.createTokenForOneHour()?.getTime()?.toString(), 'msal.idTokenExpiry');
    this.storageService.setTokenInBackend();
    this.checkIdleUsers();
    this.loggedIn = true;
    this.navigteToApplication();
  }

  isLoggedIn() {
    this.spinner.show();
    const idToken = this.storageService.getSessionStorage('msal.idtoken');
    if (idToken) {
      this.loggedIn = this.storageService.tokenValidateTime();
      if (!this.loggedIn) {
        alert('Session expired. Please login again.');
        this.storageService.logOut(false);
      } else {
        this.checkIdleUsers();
        this.navigteToApplication();
      }
    } else {
      this.loggedIn = false;
    }
    this.spinner.hide();
  }

  private navigteToApplication() {
    this.spinner.hide();
    let currentPath = window.location.pathname;

    if (currentPath !== '/prelogin' && currentPath !== '/')
      this.router.navigate([currentPath]);
    else
      this.router.navigate(['prelogin']);

  }

  showLogout() {

    this.confirmationService.confirm({
      message: 'Unsaved changes will be deleted. Do you want to continue?',
      acceptLabel: 'Yes',
      rejectLabel: 'No',
      rejectVisible: true,
      accept: () => {
        const cancelObj = {
          edit: false,
          save: true,
          cancel: true,
        };

        this.showDropdown = this.showDropdown === false ? true : false;
        this.confirmationService.close();
      },
      reject: () => {
        return;
      },
    });

  }

  logout(concurrent) {
    this.storageService.logOut(concurrent);
  }

  checkIdleUsers() {
    this.unsubscribeTimer();
    this.bnIdle.startWatching(Constants.INACTIVITY_TIMEOUT_IN_MINS * 60).subscribe((isTimedOut: boolean) => {
      if (isTimedOut) {
        this.displayBasic = true;
        this.startCountdown(60);
      }
    });
  }

  startCountdown(seconds: number) {
    this.unsubscribeTimer();

    const intervalSecond = 1000;
    const stream$ = interval(intervalSecond).pipe(take(seconds));

    this.timerSubscription = stream$.subscribe(
      (value) => {
        this.counter = seconds - value;
      },
      (error) => {
        console.error(error);
      },
      () => {
        this.onCountdownComplete();
      }
    );
  }

  onCountdownComplete() {
    this.displayBasic = false;
    this.storageService.unAuthUserFound();
  }

  continue() {
    this.unsubscribeTimer();
    this.displayBasic = false;
    // this.checkIdleUsers(); // Restart the idle check
    this.bnIdle.resetTimer();
  }

  cancel() {
    this.unsubscribeTimer();
    this.onCountdownComplete();
  }

  private unsubscribeTimer() {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
    }
  }

  ngOnDestroy(): void {
    this.unsubscribeTimer();
    this.destroy$.next();
    this.destroy$.complete();
  }

  concurrentPopup() {
      this.confirmationService.confirm({
        message: 'Concurrent session found. Would you like to logout the existing session & continue?', header: 'Confirmation',
        acceptIcon: "none", rejectIcon: "none",
        accept: () => {
          this.storageService.endPreviousSession();
        },
        reject: () => {
          this.logout(true);
        }
      });
  }
}
